import { isDefinedString, isNullOrUndefined, reduce } from "et3-type-guards";
import { isNumber, memoize, orderBy } from "lodash";
import { computed, observable, runInAction } from "mobx";

import { apiCallGetUserReferralCodeUsageData } from "../../../api/user-api/api-call-get-user-referral-code-usage-data";
import {
  REFERRAL_CODE_INITIAL_SELECTED_LIMIT,
  USER_SEARCH_BY_PLACEHOLDERS_FOR_REFERRAL_CODE_USAGE
} from "../../../constants/picker-entries-constants";
import { StringOrNumber, TUserReferralCodeUsageData } from "../../../Types";
import getCorePersonalStore from "../../core/core-personal-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../../helpers/et3-picker-data-helper";

class ReferralCodeStore {
  referralCodeUsageSearchInput = observable.box<StringOrNumber>();
  userReferralCodeUsageData = observable.box<TUserReferralCodeUsageData[]>([]);
  searchByInputForReferralCodeUsage = observable.box<string>("userId");
  referralCodeSelectedLimit = observable.box<TEt3PickerSingleValue>(
    REFERRAL_CODE_INITIAL_SELECTED_LIMIT
  );
  getInputPlaceholderForReferralCodeUsage = computed(() => {
    const picked =
      this.searchByInputForReferralCodeUsage.get() === "userId"
        ? "userId"
        : "referralCode";
    const placeholder = reduce(
      () => USER_SEARCH_BY_PLACEHOLDERS_FOR_REFERRAL_CODE_USAGE[picked],
      ""
    );
    return placeholder;
  });
  resetReferralCodeUsageData = () => {
    this.userReferralCodeUsageData.set([]);
  };
  setSearchByInputForReferralCodeUsage = (searchType: string) => {
    this.searchByInputForReferralCodeUsage.set(searchType);
  };
  setReferralCodeSelectedLimit = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.referralCodeSelectedLimit.set(limit);
    });
  };
  getUserReferralCodeUsageData = async () => {
    this.resetReferralCodeUsageData();
    const condition = this.searchByInputForReferralCodeUsage.get();
    let value = `${this.referralCodeUsageSearchInput.get()}`.trim();
    const limit = reducerToZeroIfUndefined(
      parseInt(this.referralCodeSelectedLimit.get().value, 10)
    );
    if (isNullOrUndefined(value) || !isDefinedString(value)) {
      return;
    }
    const adminUserId = getCorePersonalStore().user.get().id;
    if (
      !isNumber(adminUserId) ||
      !isDefinedString(condition) ||
      (!isNumber(value) && !isDefinedString(value))
    ) {
      getCorePopUpStore().showAlertMessage("WRONG_INPUT");
      return;
    }
    getCoreUIStore().setIsLoading("USER_REFERRAL_CODE_USAGE", true);
    try {
      const { data, status, code } = await apiCallGetUserReferralCodeUsageData({
        condition,
        value,
        limit
      });

      if (status === "OK") {
        if (data.length === 0) {
          this.userReferralCodeUsageData.set([]);
          getCoreUIStore().setIsLoading("USER_REFERRAL_CODE_USAGE", false);
          getCorePopUpStore().showAlertMessage("USER_HAS_NO_REFERRAL_CODE");
          return;
        }

        const newReferralCodeUsageData = data.map((item) => {
          const peerId = reduce(() => item.peerId, 0);
          const userId = reduce(() => item.userId, 0);
          const ownerPlatform = reduce(() => item.ownerPlatform, "");
          const userPlatform = reduce(() => item.userPlatform, "");
          const codeValidityOrReason = reduce(
            () => item.codeValidityOrReason,
            ""
          );
          const createdOn = reduce(() => item.createdOn, "");
          const referralCode = reduce(() => item.referralCode, "");
          const ownerReward = reduce(() => item.ownerReward, "");
          const userReward = reduce(() => item.userReward, "");
          return {
            createdOn: createdOn,
            peerId: peerId,
            ownerPlatform: ownerPlatform,
            referralCode: referralCode,
            ownerReward: ownerReward,
            codeValidityOrReason: codeValidityOrReason,
            userId: userId,
            userPlatform: userPlatform,
            userReward: userReward
          };
        });
        const sortedData = orderBy(
          newReferralCodeUsageData,
          "createdOn",
          "desc"
        );

        this.userReferralCodeUsageData.set(sortedData);
        getCoreUIStore().setIsLoading("USER_REFERRAL_CODE_USAGE", false);
      } else {
        getCorePopUpStore().showAlertMessage(code);
        getCoreUIStore().setIsLoading("USER_REFERRAL_CODE_USAGE", false);
      }
    } catch (error) {}
  };
}

const getReferralCodeStore = memoize(
  () => new ReferralCodeStore(),
  () => 1
);
export default getReferralCodeStore;
